import { useEffect, useMemo, useState } from 'react';
import {
  CapabilitySchema,
  type ActionReceipt,
  type Capability,
  type Mandate,
  type Profile,
  type ProposedAction,
  type RiskLevel,
} from './domain';
import { downloadJson, validateReceiptChain } from './persistence';

const formatMoney = (minor: number, currency = 'NOK') =>
  new Intl.NumberFormat('en', { style: 'currency', currency, maximumFractionDigits: 0 }).format(minor / 100);

const splitList = (value: string) => value.split(',').map((item) => item.trim()).filter(Boolean);

export function MandateEditor({ mandate, onChange }: { mandate: Mandate; onChange: (next: Mandate) => void }) {
  return <section className="workbench-card mandate-editor">
    <header><div><span>AUTHORITY CONTRACT</span><h3>Edit active mandate</h3></div><b>{mandate.id}</b></header>
    <div className="form-grid three">
      <label>Single action limit<input type="number" min="0" value={mandate.maxSpendMinor / 100} onChange={(event) => onChange({ ...mandate, maxSpendMinor: Math.round(Number(event.target.value || 0) * 100) })} /></label>
      <label>Cumulative limit<input type="number" min="0" value={mandate.maxCumulativeSpendMinor / 100} onChange={(event) => onChange({ ...mandate, maxCumulativeSpendMinor: Math.round(Number(event.target.value || 0) * 100) })} /></label>
      <label>Step-up above<input type="number" min="0" value={mandate.requiresApprovalAboveMinor / 100} onChange={(event) => onChange({ ...mandate, requiresApprovalAboveMinor: Math.round(Number(event.target.value || 0) * 100) })} /></label>
      <label>Valid until<input type="date" value={mandate.validUntil.slice(0, 10)} onChange={(event) => { if (event.target.value) onChange({ ...mandate, validUntil: new Date(`${event.target.value}T23:59:59.000Z`).toISOString() }); }} /></label>
      <label>Principal<input value={mandate.principalId} onChange={(event) => onChange({ ...mandate, principalId: event.target.value })} /></label>
      <label>Agent<input value={mandate.agentId} onChange={(event) => onChange({ ...mandate, agentId: event.target.value })} /></label>
    </div>
    <label>Allowed recipients<input value={mandate.allowedRecipients.join(', ')} onChange={(event) => onChange({ ...mandate, allowedRecipients: splitList(event.target.value) })} /></label>
    <label>Allowed purposes<textarea rows={3} value={mandate.allowedPurposes.join(', ')} onChange={(event) => onChange({ ...mandate, allowedPurposes: splitList(event.target.value) })} /></label>
    <div className="capability-editor"><span>Delegated capabilities</span><div>{CapabilitySchema.options.map((capability) => {
      const active = mandate.capabilities.includes(capability);
      return <button type="button" data-active={active} key={capability} onClick={() => onChange({ ...mandate, capabilities: active ? mandate.capabilities.filter((item) => item !== capability) : [...mandate.capabilities, capability] })}>{capability.replaceAll('_', ' ')}</button>;
    })}</div></div>
    <footer><span>{formatMoney(mandate.spentMinor, mandate.currency)} already committed</span><strong>{mandate.requiresEvidence ? 'Evidence required' : 'Evidence optional'}</strong></footer>
  </section>;
}

export function ActionComposer({
  profile,
  mandate,
  onIssue,
}: {
  profile: Profile;
  mandate: Mandate;
  onIssue: (action: ProposedAction) => void;
}) {
  const [capability, setCapability] = useState<Capability>(mandate.capabilities[0] ?? 'READ_MEMORY');
  const [purpose, setPurpose] = useState(mandate.allowedPurposes[0] ?? 'New governed action');
  const [amount, setAmount] = useState(0);
  const [recipient, setRecipient] = useState(mandate.allowedRecipients[0] ?? '');
  const [channel, setChannel] = useState(mandate.allowedChannels[0] ?? 'private');
  const [risk, setRisk] = useState<RiskLevel>('low');
  const [reversible, setReversible] = useState(true);
  const [external, setExternal] = useState(false);
  const [evidence, setEvidence] = useState('human-request');
  const [dataScopes, setDataScopes] = useState(mandate.allowedDataScopes[0] ?? '');

  useEffect(() => {
    setCapability(mandate.capabilities[0] ?? 'READ_MEMORY');
    setPurpose(mandate.allowedPurposes[0] ?? 'New governed action');
    setAmount(0);
    setRecipient(mandate.allowedRecipients[0] ?? '');
    setChannel(mandate.allowedChannels[0] ?? 'private');
    setRisk('low');
    setReversible(true);
    setExternal(false);
    setEvidence('human-request');
    setDataScopes(mandate.allowedDataScopes[0] ?? '');
  }, [mandate.id, profile]);

  const preview = useMemo<ProposedAction>(() => ({
    id: `action-${Date.now()}`,
    principalId: mandate.principalId,
    profile,
    capability,
    purpose,
    amountMinor: Math.round(amount * 100),
    currency: mandate.currency,
    recipient: recipient || undefined,
    channel: channel || undefined,
    risk,
    reversible,
    external,
    dataScopes: splitList(dataScopes),
    evidence: splitList(evidence),
    createdAt: new Date().toISOString(),
  }), [amount, capability, channel, dataScopes, evidence, external, mandate.currency, mandate.principalId, profile, purpose, recipient, reversible, risk]);

  return <section className="workbench-card action-composer">
    <header><div><span>PROPOSED ACTION</span><h3>Evaluate before action</h3></div><b>{profile}</b></header>
    <div className="form-grid two">
      <label>Capability<select value={capability} onChange={(event) => setCapability(event.target.value as Capability)}>{CapabilitySchema.options.map((item) => <option key={item} value={item}>{item.replaceAll('_', ' ')}</option>)}</select></label>
      <label>Purpose<input value={purpose} onChange={(event) => setPurpose(event.target.value)} /></label>
      <label>Amount ({mandate.currency})<input type="number" min="0" value={amount} onChange={(event) => setAmount(Number(event.target.value || 0))} /></label>
      <label>Recipient<input value={recipient} onChange={(event) => setRecipient(event.target.value)} /></label>
      <label>Channel<input value={channel} onChange={(event) => setChannel(event.target.value)} /></label>
      <label>Risk<select value={risk} onChange={(event) => setRisk(event.target.value as RiskLevel)}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select></label>
      <label>Evidence<input value={evidence} onChange={(event) => setEvidence(event.target.value)} placeholder="comma separated" /></label>
      <label>Data scopes<input value={dataScopes} onChange={(event) => setDataScopes(event.target.value)} placeholder="comma separated" /></label>
    </div>
    <div className="toggle-row"><label><input type="checkbox" checked={reversible} onChange={(event) => setReversible(event.target.checked)} /> Reversible</label><label><input type="checkbox" checked={external} onChange={(event) => setExternal(event.target.checked)} /> External consequence</label></div>
    <div className="action-preview"><span>{preview.capability}</span><strong>{preview.purpose}</strong><small>{formatMoney(preview.amountMinor, preview.currency)} · {preview.recipient || 'no recipient'} · {preview.risk} risk</small></div>
    <button className="primary-button full-button" type="button" onClick={() => onIssue({ ...preview, id: `action-${crypto.randomUUID?.() ?? Date.now()}` })}>Evaluate and issue receipt</button>
  </section>;
}

export function ReceiptLedger({ receipts, onClear }: { receipts: ActionReceipt[]; onClear: () => void }) {
  const chain = validateReceiptChain(receipts);
  return <section className="workbench-card receipt-ledger">
    <header><div><span>PROOF CHAIN</span><h3>Execution receipts</h3></div><b data-valid={chain.valid}>{chain.valid ? 'CHAIN VALID' : 'CHAIN BROKEN'}</b></header>
    {!chain.valid && <p className="chain-error">{chain.reason} · {chain.brokenAt}</p>}
    <div className="ledger-actions"><button type="button" onClick={() => downloadJson('personal-agency-os.receipts.json', receipts)}>Export receipts</button><button type="button" disabled={receipts.length === 0} onClick={onClear}>Clear local receipts</button></div>
    {receipts.length === 0 ? <p className="empty-ledger">No receipts yet. Submit a proposed action to create the first proof object.</p> : <div className="ledger-list">{receipts.map((receipt) => <article key={receipt.id}>
      <div><span className="decision" data-decision={receipt.decision}>{receipt.decision}</span><time>{new Date(receipt.createdAt).toLocaleString()}</time></div>
      <strong>{receipt.actionId}</strong><p>{receipt.reasons.join(' · ')}</p><code>{receipt.receiptHash}</code><small>previous: {receipt.previousHash}</small>
    </article>)}</div>}
  </section>;
}
